/**
 * Shared modal dialog behaviour: focus management + escape key + body
 * scroll lock. Used by `Modal` (and any future overlay primitives) so
 * they behave consistently for keyboard / screen-reader users.
 *
 * Specifically, when `open` is true the hook will:
 * - focus the first focusable element inside the dialog (or the dialog
 *   itself if none) on mount
 * - trap Tab / Shift+Tab inside the dialog (cycling between first / last
 *   focusable nodes)
 * - call `onClose()` when Escape is pressed and `dismissible` is true
 * - lock background body scroll while open
 * - restore focus to whatever was active before the dialog opened, on close
 */
import { useEffect } from 'react';

export const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isVisible(el: HTMLElement): boolean {
  if (el.hasAttribute('hidden')) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  // `checkVisibility` is supported in modern browsers and gives the correct
  // answer for display:none / visibility:hidden / opacity:0 ancestor chains.
  // In test environments (happy-dom / jsdom) it isn't implemented, so we
  // fall back to "visible" — the test selector itself is doing the work.
  if (typeof el.checkVisibility === 'function') return el.checkVisibility();
  return true;
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    // The `:not([disabled])` clauses in FOCUSABLE_SELECTOR only guard the
    // native form elements; the `a[href]` and `[tabindex]` branches do not.
    // So the `disabled` re-check is intentional — it catches a custom or
    // aria element carrying a `disabled` attribute that matched via tabindex.
    (el) => !el.hasAttribute('disabled') && isVisible(el),
  );
}

function trapTab(container: HTMLElement, event: KeyboardEvent): void {
  if (event.key !== 'Tab') return;
  const nodes = getFocusable(container);
  if (nodes.length === 0) {
    event.preventDefault();
    return;
  }
  // length > 0 guarded above; non-null assertions are safe here.
  const first = nodes[0]!;
  const last = nodes[nodes.length - 1]!;
  const active = document.activeElement as HTMLElement | null;
  if (event.shiftKey) {
    if (active === first || !container.contains(active)) {
      event.preventDefault();
      last.focus();
    }
  } else if (active === last) {
    event.preventDefault();
    first.focus();
  }
}

export interface UseDialogOptions {
  open: boolean;
  /**
   * Ref to the dialog container element. Tab cycles inside this element;
   * the initial focus call also targets its first focusable child.
   */
  containerRef: { current: HTMLElement | null };
  /** Called when Escape is pressed (only fires if `dismissible`). */
  onClose: () => void;
  /** Defaults to true. When false, Escape is ignored. */
  dismissible?: boolean;
  /**
   * Element to return focus to on close. Defaults to whatever was focused
   * when the dialog opened (correct for a Modal). Pass an explicit ref —
   * e.g. a Popover's trigger — when "previously focused" isn't reliable
   * (a pointer-opened surface may have left focus on `<body>`).
   */
  returnFocusRef?: { current: HTMLElement | null };
}

export function useDialog({
  open,
  containerRef,
  onClose,
  dismissible = true,
  returnFocusRef,
}: UseDialogOptions): void {
  // Focus management + body scroll lock
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Captured at open time (the trigger element is stable for the dialog's
    // lifetime); used in cleanup so the ref read isn't flagged as stale.
    const returnTarget = returnFocusRef?.current ?? null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const container = containerRef.current;
    if (container) {
      const firstFocusable = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? container).focus();
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      (returnTarget ?? previouslyFocused)?.focus?.();
    };
  }, [open, containerRef, returnFocusRef]);

  // Escape + Tab trap
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) {
        e.preventDefault();
        onClose();
        return;
      }
      const container = containerRef.current;
      if (container) trapTab(container, e);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, dismissible, containerRef]);
}
